/**
 * B2BQuickShare.jsx — Bouton d'accès rapide B2B dans la topbar principale.
 *
 * Visible UNIQUEMENT quand l'admin est connecté. Au clic, ouvre un dialog
 * avec le QR code prêt à être montré à un prospect en RDV physique, ou
 * à partager via WhatsApp / email.
 *
 * Comportement :
 *  - Admin se connecte sur code2roues.fr
 *  - Dans la topbar : apparaît un petit bouton "B2B" (icône QR, badge)
 *  - Clic → modal avec QR code + lien direct + raccourci vers /admin/b2b-plaquette
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { QrCode, Copy, Download, ExternalLink, Share2, Loader2 } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import api from '../api';
import { toast } from 'sonner';

export const B2BQuickShareButton = ({ compact = false }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="rounded-full h-9 gap-1.5 border-amber-400/30 bg-amber-500/5 text-amber-500 hover:bg-amber-500/10 hover:text-amber-400 hover:border-amber-400/50"
        aria-label="Partager la plaquette B2B"
        title="Partager la plaquette B2B"
        data-testid="topbar-b2b-quick-share"
      >
        <QrCode className="w-4 h-4" />
        {!compact && <span className="hidden xl:inline">B2B</span>}
      </Button>
      {open && <B2BQuickShareDialog open={open} onOpenChange={setOpen} />}
    </>
  );
};

const B2BQuickShareDialog = ({ open, onOpenChange }) => {
  const [vaultUrl, setVaultUrl] = useState('');
  const [publicBaseUrl, setPublicBaseUrl] = useState('');
  const [loading, setLoading] = useState(true);

  // L'URL partagée : /b2b (porte d'entrée) qui vérifie l'auth ou le token.
  // Par défaut, on partage le lien "générique" qui demande à l'utilisateur
  // d'entrer un code partenaire. Pour un prospect précis, l'admin peut
  // aller générer un code dédié dans /admin/b2b-plaquette.
  const shareUrl = publicBaseUrl
    ? `${publicBaseUrl}/b2b`
    : (typeof window !== 'undefined' ? `${window.location.origin}/b2b` : '/b2b');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api
      .b2bPlaquetteVaultInfo()
      .then((info) => {
        setVaultUrl(info?.vault_url || '');
        setPublicBaseUrl(info?.public_base_url || '');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open]);

  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Lien copié dans le presse-papiers.');
    } catch {
      toast.error('Impossible de copier.');
    }
  }, [shareUrl]);

  const shareNative = useCallback(async () => {
    // Web Share API — pratique sur mobile
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Code 2 Roues · Plaquette B2B',
          text: 'Découvrez notre offre B2B pour auto-écoles et moto-écoles.',
          url: shareUrl,
        });
      } catch {
        /* user cancelled */
      }
    } else {
      copyLink();
    }
  }, [shareUrl, copyLink]);

  const downloadQr = useCallback(() => {
    const canvas = document.getElementById('b2b-quick-qr-canvas');
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = 'qr-code-plaquette-b2b.png';
    a.click();
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="b2b-quick-share-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode size={18} className="text-amber-500" />
            Partager la plaquette B2B
          </DialogTitle>
          <DialogDescription>
            Montrez ce QR code à un prospect ou partagez le lien par email / WhatsApp.
            La plaquette redirige automatiquement vers la centrale de réservation.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Chargement…
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            {/* QR Code */}
            <div className="bg-white rounded-xl p-5 flex items-center justify-center" data-testid="b2b-quick-share-qr">
              <QRCodeCanvas
                id="b2b-quick-qr-canvas"
                value={shareUrl}
                size={220}
                level="M"
                fgColor="#0F172A"
                bgColor="#FFFFFF"
                includeMargin={true}
              />
            </div>

            {/* Boutons d'action */}
            <div className="grid grid-cols-3 gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={downloadQr}
                data-testid="b2b-quick-share-download"
              >
                <Download size={14} className="mr-1.5" /> PNG
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={copyLink}
                data-testid="b2b-quick-share-copy"
              >
                <Copy size={14} className="mr-1.5" /> Copier
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={shareNative}
                data-testid="b2b-quick-share-native"
              >
                <Share2 size={14} className="mr-1.5" /> Partager
              </Button>
            </div>

            {/* URL affichée */}
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Lien de la plaquette
              </Label>
              <code
                className="mt-1 block w-full px-3 py-2 rounded-lg bg-muted text-xs font-mono break-all"
                data-testid="b2b-quick-share-url"
              >
                {shareUrl}
              </code>
            </div>

            {/* Lien vers gestion complète */}
            <Link
              to="/admin/b2b-plaquette"
              onClick={() => onOpenChange(false)}
              className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-500 hover:bg-amber-500/15 transition text-xs font-bold"
              data-testid="b2b-quick-share-manage"
            >
              <ExternalLink size={14} />
              Générer un code dédié pour un prospect précis
            </Link>

            <div className="text-[11px] text-muted-foreground leading-relaxed border-t border-border/60 pt-3">
              <strong>Astuce :</strong> ce QR code ouvre la page d&apos;entrée publique
              qui demande un code d&apos;accès au visiteur. Pour partager
              directement sans code, générez un lien unique dans « Codes
              partenaires ».
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
